import { ProductImage } from "@/components/ProductImage";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Lock, FlaskConical, ShieldCheck } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";

const useIsMobile = () => {
  const [mobile, setMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 640 : false));
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
};

/**
 * Quick preview of a public product: slides up from the bottom on mobile, in from the right on desktop.
 * Shows only public data (no prices, categories or documents - those stay member-only on the server).
 */
export const ProductQuickView = ({ product, open, onOpenChange }) => {
  const navigate = useNavigate();
  const mobile = useIsMobile();
  if (!product) return null;
  const specs = product.specifications || [];
  const variants = product.variants || [];
  const go = (path) => { onOpenChange(false); navigate(path); };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side={mobile ? "bottom" : "right"}
        data-testid="product-quick-view"
        className={`flex flex-col gap-0 overflow-y-auto p-0 ${mobile ? "max-h-[88vh] rounded-t-3xl" : "w-full sm:max-w-md"}`}
      >
        {mobile && <div className="mx-auto mt-2.5 h-1.5 w-12 shrink-0 rounded-full bg-slate-200" aria-hidden="true" />}
        <div className="grid grid-cols-[112px_1fr] gap-4 px-5 pt-5 sm:grid-cols-1 sm:gap-0 sm:px-6 sm:pt-6">
          <div className="overflow-hidden rounded-2xl border border-border bg-white sm:aspect-[4/3] sm:w-full">
            {product.images?.[0] ? (
              <ProductImage src={product.images[0]} alt={product.name} loading="eager" className="h-full w-full object-cover" data-testid="quick-view-image" />
            ) : (
              <div className="flex h-28 items-center justify-center text-slate-300"><FlaskConical className="h-10 w-10" /></div>
            )}
          </div>
          <SheetHeader className="space-y-1 text-left sm:mt-5">
            <Badge className="w-fit bg-emerald-50 text-emerald-700 hover:bg-emerald-50">Research grade</Badge>
            <SheetTitle className="font-heading text-xl text-navy" data-testid="quick-view-title">{product.name}</SheetTitle>
            <SheetDescription className="font-mono text-xs">{product.sku}</SheetDescription>
          </SheetHeader>
        </div>

        <div className="px-5 pb-6 pt-4 sm:px-6">
          {product.description && (
            <p className="text-sm leading-relaxed text-muted-foreground line-clamp-4" data-testid="quick-view-description">{product.description}</p>
          )}

          {variants.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Presentations</p>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="quick-view-variants">
                {variants.map((v) => (
                  <span key={v.sku || v.name} className="rounded-lg border border-border bg-slate-50 px-2.5 py-1 text-sm font-medium text-navy">{v.name}</span>
                ))}
              </div>
            </div>
          )}

          {specs.length > 0 && (
            <div className="mt-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Specifications</p>
              <dl className="mt-2 divide-y divide-border rounded-xl border border-border" data-testid="quick-view-specs">
                {specs.map((s) => (
                  <div key={s.key} className="grid grid-cols-[110px_1fr] gap-3 px-3 py-2 text-sm">
                    <dt className="text-muted-foreground">{s.key}</dt>
                    <dd className="font-medium text-navy">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}

          <Separator className="my-5" />
          <div className="flex items-start gap-2 rounded-xl bg-navy/[0.04] p-3 text-xs text-muted-foreground">
            <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
            Pricing, availability, categories and certificates are released to approved members after login.
          </div>
        </div>

        <div className="sticky bottom-0 mt-auto grid grid-cols-2 gap-2 border-t border-border bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <Button variant="outline" onClick={() => go(`/products/${product.id}`)} data-testid="quick-view-details-button" className="border-brand-blue/30 text-brand-blue hover:bg-brand-blue/5">
            Full details <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
          <Button onClick={() => go("/login")} data-testid="quick-view-pricing-button" className="bg-navy text-white hover:bg-navy/90">
            <Lock className="mr-1.5 h-4 w-4" /> Member pricing
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
